import React from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useCreateTradingAccount } from '@/api/trading-accounts/use-trading-accounts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

export interface TradingAccountFormData {
    customer_id: string;
    base_currency: string;
    balance: number;
    leverage: number;
}

interface TradingAccountModalProps {
    open: boolean;
    onClose: () => void;
    customerId: string;
    onSuccess?: () => void;
}

const TradingAccountModal: React.FC<TradingAccountModalProps> = ({
    open,
    onClose,
    customerId,
    onSuccess,
}) => {
    const createTradingAccount = useCreateTradingAccount(customerId);
    const { enqueueSnackbar } = useSnackbar();

    const availableCurrencies = [
        { symbol: 'USD', name: 'US Dollar' },
        { symbol: 'EUR', name: 'Euro' },
    ];

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setValue,
        setError,
        watch,
    } = useForm<TradingAccountFormData>({
        defaultValues: {
            customer_id: customerId,
            base_currency: availableCurrencies?.[0]?.symbol || 'USD',
            balance: 0,
            leverage: 100,
        },
    });

    React.useEffect(() => {
        if (open) {
            reset({
                customer_id: customerId,
                base_currency: availableCurrencies?.[0]?.symbol || 'USD',
                balance: 0,
                leverage: 100,
            });
        }
    }, [open, customerId, reset]);

    const handleFormSubmit = async (data: TradingAccountFormData) => {
        try {
            await createTradingAccount.mutateAsync(data);
            enqueueSnackbar('Trading account created successfully', { variant: 'success' });
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            // Try to surface validation errors inline and as a toast
            const apiErrors = error?.response?.data?.errors;
            const message = error?.response?.data?.message || 'Failed to create trading account';

            if (apiErrors) {
                if (apiErrors.base_currency && apiErrors.base_currency[0]) {
                    // Set field error for currency
                    setError('base_currency', {
                        type: 'server',
                        message: apiErrors.base_currency[0],
                    });
                }
            }
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create Trading Account</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="base_currency">Base Currency</Label>
                        <Select
                            value={watch('base_currency')}
                            onValueChange={value =>
                                setValue('base_currency', value, { shouldValidate: true })
                            }
                            disabled={isSubmitting || createTradingAccount.isPending}
                        >
                            <SelectTrigger
                                className={errors.base_currency ? 'border-destructive' : ''}
                            >
                                <SelectValue placeholder={'Select currency'} />
                            </SelectTrigger>
                            <SelectContent>
                                {availableCurrencies?.map(currency => (
                                    <SelectItem key={currency.symbol} value={currency.symbol}>
                                        {currency.symbol} - {currency.name}
                                    </SelectItem>
                                )) || (
                                    <SelectItem value="none" disabled>
                                        {'No currencies available'}
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        {errors.base_currency && (
                            <p className="text-sm text-destructive">
                                {errors.base_currency.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="balance">Initial Balance</Label>
                        <Input
                            id="balance"
                            type="number"
                            step="0.01"
                            {...register('balance', {
                                valueAsNumber: true,
                                required: 'Initial balance is required',
                                min: {
                                    value: 0,
                                    message: 'Balance must be a positive number',
                                },
                            })}
                            className={errors.balance ? 'border-destructive' : ''}
                            disabled={isSubmitting || createTradingAccount.isPending}
                        />
                        {errors.balance && (
                            <p className="text-sm text-destructive">{errors.balance.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="leverage">Leverage</Label>
                        <Input
                            id="leverage"
                            type="number"
                            min="1"
                            max="2000"
                            {...register('leverage', {
                                valueAsNumber: true,
                                required: 'Leverage is required',
                                min: {
                                    value: 1,
                                    message: 'Leverage must be at least 1',
                                },
                                max: {
                                    value: 2000,
                                    message: 'Leverage cannot exceed 2000',
                                },
                            })}
                            className={errors.leverage ? 'border-destructive' : ''}
                            disabled={isSubmitting || createTradingAccount.isPending}
                        />
                        {errors.leverage && (
                            <p className="text-sm text-destructive">{errors.leverage.message}</p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isSubmitting || createTradingAccount.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting || createTradingAccount.isPending}
                        >
                            {isSubmitting || createTradingAccount.isPending
                                ? 'Creating...'
                                : 'Create Account'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default TradingAccountModal;
